import { first_truthy, normalize_text } from './view_case_details_core_helpers';
import { extension_from_name, filename_from_url, format_file_size } from './view_case_details_record_document_helpers';

export const REASONING_TRAIL_PREFIX = '[ReasoningTrail]';

export function format_note_timestamp(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalize_text(value);

  return `${date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
  })} - ${date.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;
}

export function normalize_note(note = {}, index = 0) {
  const author = note_author(note);
  const timestamp = normalize_text(first_truthy([
    note?.timestamp,
    format_note_timestamp(note?.date_created || note?.journal_date || note?.created_at),
  ]));

  return {
    id: note?.id || `${author}-${timestamp}-${index}`,
    author,
    timestamp,
    body: normalize_text(note?.body || note?.journal_description || note?.description || note?.note),
  };
}

function note_author(note) {
  return normalize_text(first_truthy([
    note?.author,
    full_name(note?.user_first_name, note?.user_last_name),
    note?.user_full_name,
    note?.created_by_name,
    'Unknown user',
  ]));
}

function full_name(first_name, last_name) {
  return [first_name, last_name].map(normalize_text).filter(Boolean).join(' ');
}

export function is_note_journal(note = {}) {
  const hours = Number(first_truthy([note?.hours_spent, note?.hours, 0]));
  const description = journal_description_text(note);
  const is_time_description = /^(not billable|billable)\s*[-:]/i.test(description);

  return has_no_billable_hours(hours) && !is_time_description && !is_reasoning_journal(note);
}

function has_no_billable_hours(hours) {
  return !Number.isFinite(hours) || hours <= 0;
}

export function reasoning_display_body(note = {}) {
  const description = journal_description_text(note);

  if (!description.startsWith(REASONING_TRAIL_PREFIX)) {
    return description;
  }

  return normalize_text(description.slice(REASONING_TRAIL_PREFIX.length));
}

export function is_reasoning_journal(note = {}) {
  return journal_description_text(note).startsWith(REASONING_TRAIL_PREFIX)
    && has_no_billable_hours(Number(first_truthy([note?.hours_spent, note?.hours, 0])));
}

export function normalize_reasoning_item(note = {}, index = 0) {
  const timestamp_source = first_truthy([
    note?.date_created,
    note?.journal_date,
    note?.created_at,
    note?.timestamp,
  ]);
  const timestamp = timestamp_source ? new Date(timestamp_source) : null;
  const is_valid_timestamp = timestamp instanceof Date && !Number.isNaN(timestamp.getTime());

  return {
    id: note?.id || `reasoning-${index}`,
    date: is_valid_timestamp
      ? timestamp.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
      : '',
    time: is_valid_timestamp
      ? timestamp.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false })
      : '',
    type: 'Human',
    tag_class: 'case-details-tag-human',
    body: reasoning_display_body(note),
  };
}

function journal_description_text(note = {}) {
  return normalize_text(first_truthy([
    note?.journal_description,
    note?.description,
    note?.body,
    note?.note,
  ]));
}

export function split_journal_description(description = '') {
  const normalized = normalize_text(description);
  const match = normalized.match(/^(not billable|billable)\s*[-:]\s*(.*)$/i);
  if (!match) return { activity: normalized, billable: false };

  return {
    activity: normalize_text(match[2]),
    billable: match[1].toLowerCase() !== 'not billable',
  };
}

export function decimal_hours_from_display(value) {
  const normalized = normalize_text(value);
  const hours_minutes = normalized.match(/^(\d+):([0-5]\d)$/);
  if (!hours_minutes) return decimal_hours(normalized);

  return Number(hours_minutes[1]) + Number(hours_minutes[2]) / 60;
}

function decimal_hours(normalized) {
  const decimal = Number(normalized.replace('h', ''));
  return Number.isFinite(decimal) ? decimal : 0;
}

export function format_hours_count(value) {
  const hours = Number(value || 0);
  if (!Number.isFinite(hours)) return '0';

  return Number(hours.toFixed(2)).toString();
}

export function normalize_time_journal(journal = {}, index = 0) {
  const description = split_journal_description(
    journal?.journal_description || journal?.activity || journal?.description
  );
  const hours_value = first_truthy([journal?.hours_spent, journal?.hours, journal?.hours_spent_display]);
  const decimal_hours_value = decimal_hours_from_display(hours_value);
  const date_label = time_journal_date_label(journal);
  const evidence_files = normalize_time_journal_documents(journal);
  const evidence_count = Number(first_truthy([
    evidence_files.length,
    journal?.evidence_count,
    journal?.documents_count,
    journal?.document_count,
    journal?.attachments_count,
  ])) || evidence_files.length;

  return {
    id: journal?.id || `${date_label}-${description.activity}-${index}`,
    date: date_label,
    activity: description.activity,
    owner: time_journal_owner(journal),
    billable: description.billable,
    hours: decimal_hours_value ? `${Number(decimal_hours_value.toFixed(2))}h` : normalize_text(hours_value),
    hours_value: decimal_hours_value,
    evidence_files,
    evidence_count,
  };
}

function normalize_time_journal_documents(journal = {}) {
  const raw_documents = time_journal_document_candidates(journal);
  return raw_documents
    .map((document, index) => normalize_time_journal_document(document, journal, index))
    .filter((document) => document.name || document.url);
}

function time_journal_document_candidates(journal = {}) {
  const candidates = [
    journal?.documents,
    journal?.evidence_files,
    journal?.evidence,
    journal?.attachments,
    journal?.files,
    journal?.journal_documents,
    journal?.supporting_documents,
    journal?.supporting_evidence,
  ];

  const collection = candidates.find((value) => Array.isArray(value) && value.length);
  if (collection) return collection;

  return [];
}

function normalize_time_journal_document(document = {}, journal = {}, index = 0) {
  const url = normalize_text(document?.signed_file_url || document?.preview_url || document?.file_url || document?.url);
  const name = normalize_text(document?.document_name || document?.file_name || document?.name || filename_from_url(url) || `Evidence ${index + 1}`);
  const extension = normalize_text(document?.extension || document?.file_type || extension_from_name(name || url)).toUpperCase();
  const size = format_file_size(first_truthy([
    document?.file_size,
    document?.size,
    document?.content_length,
  ]));

  return {
    ...document,
    id: document?.id || `${journal?.id || 'journal'}-evidence-${index}`,
    name,
    extension,
    file_type: extension,
    size,
    url,
    source: 'journal',
    uploaded_by: normalize_text(document?.uploaded_by),
    date_uploaded: normalize_text(document?.date_uploaded),
    upload_status: normalize_text(document?.upload_status || document?.status || 'Uploaded'),
  };
}

function time_journal_owner(journal) {
  return normalize_text(first_truthy([
    journal?.owner,
    full_name(journal?.user_first_name, journal?.user_last_name),
    journal?.user_full_name,
    'Unknown user',
  ]));
}

function time_journal_date_label(journal) {
  const raw_date = journal?.journal_date || journal?.date_created || journal?.created_at;
  const date = new Date(raw_date);
  return Number.isNaN(date.getTime())
    ? normalize_text(raw_date)
    : date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
}
